// ═══════════════════════════════════════════════════════════════════════════════
// Calendar day picker — stores values as ISO calendar strings (YYYY-MM-DD) for API
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { DayPicker, getDefaultClassNames, type Matcher } from 'react-day-picker';
import { zhCN, zhTW, enUS } from 'date-fns/locale';
import { formatDate, LANGUAGE, parseDate, type Language } from '@fdh/shared';
import { useLanguage } from '../contexts/LanguageContext';
import { CalendarIcon } from './CalendarIcon';

export type IsoDatePickerProps = {
  id: string;
  /** Visible field label; omit when using only ariaLabel (e.g. compact admin filters) */
  label?: string;
  /** Required when label is omitted — passed to the trigger button for a11y */
  ariaLabel?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  /** Optional helper line under the control */
  hint?: string;
  /** default: light form surface; admin: dark admin portal toolbar */
  appearance?: 'default' | 'admin';
  className?: string;
  /**
   * When set to dropdown variants, month/year can be chosen from selects (e.g. birthdays).
   * `startMonth` / `endMonth` are derived from minDate/maxDate when provided.
   */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years';
  valueFormat?: 'localized' | 'iso';
};

const localeForAppLanguage = (language: Language) => {
  if (language === LANGUAGE.ZH_HANT) return zhTW;
  if (language === LANGUAGE.ZH_HANS) return zhCN;
  return enUS;
};

const buildDisabledMatchers = (minDate?: string, maxDate?: string): Matcher | Matcher[] | undefined => {
  const matchers: Matcher[] = [];
  const minParsed = minDate ? parseDate(minDate) : null;
  const maxParsed = maxDate ? parseDate(maxDate) : null;
  if (minParsed) {
    matchers.push({ before: minParsed });
  }
  if (maxParsed) {
    matchers.push({ after: maxParsed });
  }
  return matchers.length > 0 ? matchers : undefined;
};

export const IsoDatePicker = ({
  id,
  label,
  ariaLabel,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
  hint,
  appearance = 'default',
  className = '',
  captionLayout: captionLayoutProp = 'label',
  valueFormat = 'localized',
}: IsoDatePickerProps) => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const defaultClassNames = getDefaultClassNames();

  const VIEWPORT_PAD_PX = 8;
  const GAP_BELOW_TRIGGER_PX = 4;

  const updatePopoverPlacement = useCallback(() => {
    const popEl = popoverRef.current;
    const btnEl = buttonRef.current;
    if (!popEl || !btnEl) return;
    const triggerRect = btnEl.getBoundingClientRect();
    const popRect = popEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = triggerRect.left;
    if (left + popRect.width > vw - VIEWPORT_PAD_PX) {
      left = vw - VIEWPORT_PAD_PX - popRect.width;
    }
    if (left < VIEWPORT_PAD_PX) {
      left = VIEWPORT_PAD_PX;
    }
    let top = triggerRect.bottom + GAP_BELOW_TRIGGER_PX;
    const spaceBelow = vh - VIEWPORT_PAD_PX - top;
    const spaceAbove = triggerRect.top - VIEWPORT_PAD_PX;
    if (spaceBelow < popRect.height && spaceAbove >= popRect.height) {
      top = triggerRect.top - popRect.height - GAP_BELOW_TRIGGER_PX;
    }
    if (top + popRect.height > vh - VIEWPORT_PAD_PX) {
      top = Math.max(VIEWPORT_PAD_PX, vh - VIEWPORT_PAD_PX - popRect.height);
    }
    if (top < VIEWPORT_PAD_PX) {
      top = VIEWPORT_PAD_PX;
    }
    popEl.style.position = 'fixed';
    popEl.style.top = `${top}px`;
    popEl.style.left = `${left}px`;
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const runPlacement = () => {
      updatePopoverPlacement();
    };
    runPlacement();
    const popEl = popoverRef.current;
    const ro = popEl ? new ResizeObserver(runPlacement) : null;
    if (popEl) ro?.observe(popEl);
    window.addEventListener('scroll', runPlacement, true);
    window.addEventListener('resize', runPlacement);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', runPlacement, true);
      window.removeEventListener('resize', runPlacement);
    };
  }, [open, updatePopoverPlacement, language, value, captionLayoutProp]);

  const selectedDate = parseDate(value) ?? undefined;
  const defaultMonth = selectedDate ?? new Date();
  const pickerLocale = localeForAppLanguage(language);
  const disabledMatchers = buildDisabledMatchers(minDate, maxDate);

  const dropdownCaptionEnabled = captionLayoutProp !== 'label';
  const minParsedForNav = minDate ? parseDate(minDate) : null;
  const maxParsedForNav = maxDate ? parseDate(maxDate) : null;
  const pickerStartMonth = dropdownCaptionEnabled
    ? minParsedForNav
      ? new Date(minParsedForNav.getFullYear(), minParsedForNav.getMonth(), 1)
      : new Date(new Date().getFullYear() - 120, 0, 1)
    : undefined;
  const pickerEndMonth = dropdownCaptionEnabled
    ? maxParsedForNav
      ? new Date(maxParsedForNav.getFullYear(), maxParsedForNav.getMonth(), 1)
      : undefined
    : undefined;

  const buttonAriaLabelIfUnlabeled = ariaLabel ?? t('common.openCalendarAria');

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      handleClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleSelect = (d: Date | undefined) => {
    if (!d) return;
    onChange(formatDate(d));
    handleClose();
  };

  const parsed = parseDate(value);
  const displayText = parsed
    ? (valueFormat === 'iso' ? value : format(parsed, 'PPP', { locale: pickerLocale }))
    : t('common.datePlaceholder');

  const labelClass =
    appearance === 'admin'
      ? 'block text-sm font-medium text-gray-300'
      : 'block text-sm font-medium text-gray-700 dark:text-gray-200';

  const buttonClassBody =
    appearance === 'admin'
      ? [
          'flex min-h-11 w-full min-w-[10rem] items-center justify-between gap-2 rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-left text-sm text-gray-100 shadow-sm',
          'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
        ].join(' ')
      : [
          'flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-gray-900 shadow-sm',
          'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100',
        ].join(' ');

  const buttonClass = [label ? 'mt-1' : '', buttonClassBody].filter(Boolean).join(' ');

  const popoverClass =
    appearance === 'admin'
      ? 'iso-date-picker-popover fixed z-[100] max-w-[calc(100vw-1rem)] rounded-xl border border-gray-600 bg-gray-900 p-3 shadow-lg'
      : 'iso-date-picker-popover fixed z-50 max-w-[calc(100vw-1rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-900';

  const hintClass =
    appearance === 'admin' ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500 dark:text-gray-400';

  return (
    <div ref={containerRef} className={['relative', className].filter(Boolean).join(' ')}>
      {label ? (
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label ? undefined : buttonAriaLabelIfUnlabeled}
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleToggleKeyDown}
        className={buttonClass}
      >
        <span className="text-sm">{displayText}</span>
        <CalendarIcon className="h-5 w-5 shrink-0" />
      </button>
      {hint ? <p className={hintClass}>{hint}</p> : null}

      {open ? (
        <div
          ref={popoverRef}
          className={popoverClass}
          role="dialog"
          aria-label={label ? `${label} — ${t('common.openCalendarAria')}` : buttonAriaLabelIfUnlabeled}
        >
          <DayPicker
            mode="single"
            required={false}
            selected={selectedDate}
            onSelect={handleSelect}
            defaultMonth={defaultMonth}
            locale={pickerLocale}
            disabled={disabledMatchers}
            captionLayout={captionLayoutProp}
            startMonth={pickerStartMonth}
            endMonth={pickerEndMonth}
            {...(dropdownCaptionEnabled ? { reverseYears: true } : {})}
            classNames={{
              ...defaultClassNames,
              root: `${defaultClassNames.root} rdp-root ${appearance === 'admin' ? 'text-gray-100' : 'text-gray-900 dark:text-gray-100'}`,
              caption_label: `${defaultClassNames.caption_label ?? ''} font-medium dark:text-gray-100`,
              weekday: `${defaultClassNames.weekday ?? ''} text-gray-600 dark:text-gray-300`,
              day: `${defaultClassNames.day ?? ''} dark:text-gray-100`,
              day_button: `${defaultClassNames.day_button ?? ''} dark:hover:bg-gray-800`,
              selected: `${defaultClassNames.selected} !bg-primary-600 !text-white`,
              today: `${defaultClassNames.today} font-semibold text-primary-700 dark:text-primary-300`,
              dropdown: `${defaultClassNames.dropdown ?? ''} rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`,
              dropdowns: `${defaultClassNames.dropdowns ?? ''} flex flex-wrap items-center gap-2`,
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
